import React, { useRef, useEffect, useState } from 'react';
import { func, shape, string, array } from 'prop-types';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import { Tooltip } from '@andes/tooltip';

import { IconDrawPolygon } from '../../../../components-v2/icons/sources';
import { NAMESPACE, CONTROL_POSITION } from './constants';
import helper from '../helpers';
import { trackClickEvent } from '../../track';
import useMapConfig from '../../hooks/use-map-config';
import useTimesViewed from '../../../../hooks/use-times-viewed';
import { POLYGON_EVENTS } from '../constants';

const { getControlPosition } = helper;

const namespace = `${NAMESPACE}-draw-button`;

const { IDLE, DRAWING, DRAWN } = POLYGON_EVENTS;

/**
 * Component to render the draw toggler control on the map.
 * Displays different buttons based on the current drawing state and can show an informative tooltip.
 *
 * @param {Object} props
 * @param {string} props.polygonTogglerState - Current state of polygon drawing ('IDLE', 'DRAWING', or 'DRAWN').
 * @param {Function} props.handleDrawable - Callback to toggle polygon drawing mode.
 * @param {Object} [props.map] - Map instance with a controls array to mount the button.
 * @param {string} [props.position=CONTROL_POSITION.rightTop] - Position of the control on the map.
 * @param {string} [props.iconColor] - Color of the draw button icon.
 * @returns {React.Component|null} The draw toggler component or null if in DRAWING state.
 */

const DrawToggler = ({ polygonTogglerState, handleDrawable, map, position = CONTROL_POSITION.rightTop, iconColor }) => {
  const {
    controls: { drawPolygon = {}, removePolygon = {} },
  } = useMapConfig();

  const enableLabel = drawPolygon.action?.label?.text;
  const clearLabel = removePolygon.action?.label?.text;
  const tooltipInfo = drawPolygon.andes_tooltip;
  const enableTrackInfo = drawPolygon.action?.track;
  const clearTrackInfo = removePolygon.action?.track;

  const { isAvailable, handleClose, wasClosed } = useTimesViewed(tooltipInfo ? drawPolygon?.type : null);

  const drawTogglerRef = useRef(document.createElement('div'));
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const mapControls = map.controls[getControlPosition(position)];
    const controlIndex = mapControls.length;

    mapControls.push(drawTogglerRef.current);

    return () => {
      mapControls.splice(controlIndex, 1);
    };
  }, [map, position, drawTogglerRef]);

  useEffect(() => {
    if (tooltipInfo && isAvailable && !wasClosed) {
      const timer = setTimeout(() => setShowTooltip(true), 1000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [tooltipInfo, isAvailable, wasClosed]);

  if (!enableLabel) {
    return null;
  }

  const onClick = () => {
    if (enableTrackInfo || clearTrackInfo) {
      trackClickEvent(null, polygonTogglerState === IDLE ? enableTrackInfo?.melidata : clearTrackInfo?.melidata);
    }

    if (polygonTogglerState === IDLE) {
      setShowTooltip(false);
      handleClose();
    }

    setTimeout(() => {
      handleDrawable();
    }, 0);
  };

  const renderEnableButton = () =>
    enableLabel && (
      <button
        type="button"
        id="draw-polygon"
        onClick={onClick}
        className={classNames(`${namespace}-button ${namespace}-button-enable`)}
      >
        <IconDrawPolygon fill={iconColor} className={`${namespace}-button-icon`} />
        {enableLabel}
      </button>
    );

  const renderClearButton = () =>
    clearLabel && (
      <button
        type="button"
        id="draw-polygon"
        onClick={onClick}
        className={classNames(`${namespace}-button ${namespace}-button-clear`)}
      >
        <span className={`${namespace}-button-icon`}>X</span>
        {clearLabel}
      </button>
    );

  if (!drawTogglerRef || polygonTogglerState === DRAWING) {
    return null;
  }

  return createPortal(
    <div className={`${namespace}-container`}>
      {polygonTogglerState === IDLE &&
        (tooltipInfo && isAvailable && !wasClosed ? (
          <Tooltip
            title={tooltipInfo.title}
            content={tooltipInfo.body}
            open={showTooltip}
            onClose={() => {
              setShowTooltip(false);
              handleClose();
            }}
            type="highlight"
            closable
          >
            {renderEnableButton()}
          </Tooltip>
        ) : (
          renderEnableButton()
        ))}
      {polygonTogglerState === DRAWN && renderClearButton()}
    </div>,
    drawTogglerRef.current,
  );
};

DrawToggler.propTypes = {
  handleDrawable: func.isRequired,
  iconColor: string,
  map: shape({
    controls: array,
  }),
  polygonTogglerState: string.isRequired,
  position: string,
};

DrawToggler.defaultProps = {
  iconColor: undefined,
  map: null,
  position: CONTROL_POSITION.rightTop,
};

export default DrawToggler;
